import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Typography,
    TextField,
    useMediaQuery,
    useTheme,
    InputLabel,
    FormControl,
    MenuItem,
    Select
} from '@mui/material';
import {
    LocalizationProvider,
    StaticDateTimePicker,
    MobileDatePicker,
    DesktopDatePicker
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { addMentorTimeSlot, getMentorTimeSlot } from '../services';
import { decryptId } from '../utils/decyption';
import { safeDecryptId } from '../utils/safeDecryptId';

const AddtimeSlot = () => {
    const { id: encryptedId } = useParams();
    const secretKey = process.env.REACT_APP_SECRET_KEY;
    const navigate = useNavigate();
    const [mentorId, setMentorId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [timeSlots, setTimeSlots] = useState([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTimeZone, setSelectedTimeZone] = useState('UTC');

    // const handleDateChange = (newValue) => {
    //   setSelectedDate(newValue);
    // };

    const handleConfirmDzone = () => {
        console.log('Selected Date:', selectedDate?.toString());
        console.log('Selected Time Zone:', selectedTimeZone);
        // Optional: convert selectedDate to selectedTimeZone here
    };

    // Decrypt id on mount or when encryptedId changes
    useEffect(() => {
        const decryptedId = safeDecryptId(encryptedId, secretKey);

        if (!decryptedId) {
            // Invalid or tampered URL, redirect kar do
            navigate("/unauthorized");
            return;
        }
        setMentorId(decryptedId);
    }, [encryptedId, secretKey]);

    // Fetch mentor slots when mentorId or selectedDate changes
    useEffect(() => {
        if (!mentorId) return;

        const fetchData = async (dateToSend = selectedDate) => {
            try {
                const formattedDate = dayjs(dateToSend).format('YYYY-MM-DD');
                const res = await getMentorTimeSlot(mentorId, formattedDate);
                setTimeSlots(res?.data || []);
            } catch (err) {
                console.error('Error fetching mentor time slots:', err);
            }
        };

        fetchData();
    }, [mentorId, selectedDate]);

    // On date change handler
    const handleDateChange = (newValue) => {
        setSelectedDate(newValue);
    };

    // On confirm add timeslot
    const handleConfirmDate = async () => {
        if (!mentorId || !selectedDate) return;

        const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
        const formattedTime = dayjs(selectedDate).format('HH:mm:ss');

        const payload = {
            refmentorid: parseInt(mentorId),
            slot_time: formattedTime,
            date: formattedDate,
            timezone: selectedTimeZone
        };

        try {
            await addMentorTimeSlot(payload);
            // Refresh list after add
            const res = await getMentorTimeSlot(mentorId, formattedDate);
            setTimeSlots(res?.data || []);
        } catch (error) {
            console.error('Error adding time slot:', error);
        }
    };
    const timeZones = [
        'Canada',
        'Europe',
        'UK',
        "US"
    ];

    return (
        <Grid container spacing={10} sx={{ minHeight: '100vh', backgroundColor: '#E4F2F9', paddingLeft: '50px' }}>
            {/* Left side - Date Picker */}
            <Grid item sm={6}>
                <CardHeader
                    title="Add Time Slot"
                    titleTypographyProps={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: "1.25rem",
                        textAlign: "center"
                    }}
                    sx={{ paddingY: 2,mt:"35px" }}
                />
                <Card
                    sx={{
                        minHeight: '80vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50px',
                        maxWidth: { xs: "100%", sm: "500px" }, // Full width on mobile, limit on desktop
                        overflowX: "auto",

                    }}
                >
                    <CardHeader></CardHeader>
                    <CardContent >
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
                            <FormControl fullWidth sx={{ maxWidth: 300, alignItems: "left" }}>
                                <InputLabel sx={{ fontFamily: "Poppins, sans-serif" }}>Time Zone</InputLabel>
                                <Select
                                    value={selectedTimeZone}
                                    onChange={(e) => setSelectedTimeZone(e.target.value)}
                                    label="Time Zone"
                                >
                                    {timeZones.map((zone) => (
                                        <MenuItem key={zone} value={zone}>
                                            {zone}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" color="red">
                                * If no time zone is selected, Asia/Kolkata will be used by default
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <StaticDateTimePicker
                                    sx={{
                                        xs: {

                                        }
                                    }}
                                    orientation={isMobile ? "portrait" : "landscape"}
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    onAccept={handleConfirmDate}
                                    slotProps={{
                                        actionBar: {
                                            actions: [], // Only show OK
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                            <Button variant="contained" onClick={handleConfirmDate}>
                                Ok
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Right side - Time Slot List */}
            <Grid item sm={6} padding="20px">
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mt="15px" mb="20px">
                    {/* <Typography variant="h4">Time Slot</Typography> */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {isMobile ? (
                            <MobileDatePicker
                                label="Select date"
                                value={selectedDate}
                                onChange={handleDateChange}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        ) : (
                            <DesktopDatePicker
                                label="Select date"
                                value={selectedDate}
                                onChange={handleDateChange}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        )}
                    </LocalizationProvider>
                </Box>

                <Grid container spacing={2}>
                    {timeSlots.length > 0 ? (
                        timeSlots.map((item, index) => (
                            <Grid item xs={6} key={index}>
                                <Card sx={{
                                    backgroundColor: item.is_booked === 1 ? '#1DB954' : 'white',
                                }}>
                                    <CardContent>
                                        <Typography variant="h6">
                                            {dayjs(item.hour.slot_time, 'HH:mm:ss').format('h:mm A')}
                                            <div>{item.date}</div>
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        <Typography sx={{ mt: 2 }}>No time slots available for this date.</Typography>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AddtimeSlot;
